import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import {
  PREVIEW_ALL,
  PREVIEW_NONE,
  PREVIEW_PARAMS_NAME,
  VOICE_KNOWLEDGE_COACHING_FEATURE_FLAG,
} from './constants';
import {
  buildLiveOnlyContentSnippetFilters,
  buildLiveOnlyFilters,
  ensureCurrentContentSnippetInFilters,
  ensureCurrentInstructionInFilters,
} from './helpers';
import { useGetInstructionsQuery } from 'src/services/ai-instruction/aiInstructionApi';
import { useGetBrandsQuery } from 'src/services/brand/brandApi';
import { useListContentSnippetsQuery } from 'src/services/content-snippets/contentSnippetsApi';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';
import {
  selectAiInstructionFilters,
  selectContentSnippetFilters,
} from 'src/slices/workflow-preview/workflowPreviewSlice';
import type { Brand } from 'src/types/brandTypes';

type KnowledgeCoachingBrandGroup = {
  brands: Brand[];
  category: 'Widget' | 'Voice';
};

export const useKnowledgeCoachingBrandGroups = () => {
  const { data: widgetBrands = [] } = useGetBrandsQuery({
    brandType: 'solve-widget',
  });
  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isVoiceKnowledgeCoachingEnabled = featureFlags.includes(
    VOICE_KNOWLEDGE_COACHING_FEATURE_FLAG,
  );
  const { data: voiceBrands = [] } = useGetBrandsQuery(
    {
      brandType: 'solve-voice',
    },
    {
      skip: !isVoiceKnowledgeCoachingEnabled,
    },
  );

  const brandGroups = useMemo<KnowledgeCoachingBrandGroup[]>(
    () => [
      {
        brands: widgetBrands,
        category: 'Widget',
      },
      ...(isVoiceKnowledgeCoachingEnabled
        ? [
            {
              brands: voiceBrands,
              category: 'Voice' as const,
            },
          ]
        : []),
    ],
    [isVoiceKnowledgeCoachingEnabled, voiceBrands, widgetBrands],
  );
  const brands = useMemo(
    () => brandGroups.flatMap(group => group.brands),
    [brandGroups],
  );

  return {
    brandGroups,
    brands,
    isVoiceKnowledgeCoachingEnabled,
  };
};

export const useGetAiInstructions = ({
  currentInstructionId,
}: {
  currentInstructionId?: string;
}) => {
  const { data: aiInstructionsPublished = [] } = useGetInstructionsQuery({
    draftMode: 'published',
  });
  const { data: aiInstructionsDraft = [] } = useGetInstructionsQuery({
    draftMode: 'draft',
  });

  const aiInstructions = useMemo(() => {
    const result = [...aiInstructionsPublished];

    if (currentInstructionId) {
      const currentDraft = aiInstructionsDraft.find(
        draft => draft.ai_instruction_id === currentInstructionId,
      );

      if (currentDraft) {
        const existingIndex = result.findIndex(
          instr => instr.ai_instruction_id === currentInstructionId,
        );

        const draftInstruction = {
          ...currentDraft,
          is_draft: existingIndex === -1,
        };

        if (existingIndex >= 0) {
          result[existingIndex] = draftInstruction;
        } else {
          result.push(draftInstruction);
        }
      }
    }

    return result;
  }, [aiInstructionsDraft, aiInstructionsPublished, currentInstructionId]);

  return aiInstructions;
};

export const useGetContentSnippets = ({
  currentContentSnippetId,
}: {
  currentContentSnippetId?: string;
}) => {
  const { data: publishedContentSnippetsData } = useListContentSnippetsQuery({
    draftMode: 'published',
  });
  const publishedContentSnippets = useMemo(() => {
    return publishedContentSnippetsData?.snippets || [];
  }, [publishedContentSnippetsData?.snippets]);

  const { data: draftContentSnippetsData } = useListContentSnippetsQuery({
    draftMode: 'draft',
  });
  const draftContentSnippets = useMemo(() => {
    return draftContentSnippetsData?.snippets || [];
  }, [draftContentSnippetsData?.snippets]);

  const contentSnippets = useMemo(() => {
    const result = [...publishedContentSnippets];

    if (currentContentSnippetId) {
      const currentDraft = draftContentSnippets.find(
        draft => draft.content_snippet_id === currentContentSnippetId,
      );

      if (currentDraft) {
        const existingIndex = result.findIndex(
          snippet => snippet.content_snippet_id === currentContentSnippetId,
        );

        const draftSnippet = {
          ...currentDraft,
          is_draft: existingIndex === -1,
        };

        if (existingIndex >= 0) {
          result[existingIndex] = draftSnippet;
        } else {
          result.push(draftSnippet);
        }
      }
    }

    return result;
  }, [draftContentSnippets, publishedContentSnippets, currentContentSnippetId]);

  return contentSnippets;
};

export const useAiInstructionFilters = () => {
  const [params] = useSearchParams();
  const aiInstructionPreviewMode = params.get(PREVIEW_PARAMS_NAME);
  const currentInstructionId =
    aiInstructionPreviewMode === PREVIEW_NONE ||
    aiInstructionPreviewMode === PREVIEW_ALL
      ? undefined
      : aiInstructionPreviewMode;
  const currentAiInstructionFilters = useSelector(selectAiInstructionFilters);
  const aiInstructions = useGetAiInstructions({
    currentInstructionId: currentInstructionId ?? undefined,
  });

  return useMemo(() => {
    if (currentAiInstructionFilters) {
      return currentAiInstructionFilters;
    }

    return ensureCurrentInstructionInFilters({
      aiInstructions,
      currentFilters: buildLiveOnlyFilters(aiInstructions),
      currentInstructionId,
    });
  }, [aiInstructions, currentAiInstructionFilters, currentInstructionId]);
};

export const useContentSnippetFilters = () => {
  const [params] = useSearchParams();
  const contentSnippetPreviewMode = params.get(PREVIEW_PARAMS_NAME);
  const currentContentSnippetId =
    contentSnippetPreviewMode === PREVIEW_NONE ||
    contentSnippetPreviewMode === PREVIEW_ALL
      ? undefined
      : contentSnippetPreviewMode;
  const currentContentSnippetFilters = useSelector(selectContentSnippetFilters);
  const contentSnippets = useGetContentSnippets({
    currentContentSnippetId: currentContentSnippetId ?? undefined,
  });

  return useMemo(() => {
    if (currentContentSnippetFilters) {
      return currentContentSnippetFilters;
    }

    return ensureCurrentContentSnippetInFilters({
      contentSnippets,
      currentContentSnippetId,
      currentFilters: buildLiveOnlyContentSnippetFilters(contentSnippets),
    });
  }, [contentSnippets, currentContentSnippetFilters, currentContentSnippetId]);
};

export const useIsLiveOnly = () => {
  const aiInstructions = useGetAiInstructions({
    currentInstructionId: undefined,
  });
  const currentAiInstructionFilters = useAiInstructionFilters();

  return useMemo(() => {
    return currentAiInstructionFilters.every(filter => {
      const instruction = aiInstructions.find(
        instr => instr.ai_instruction_id === filter.aiInstructionId,
      );
      return !filter.isDraft && instruction?.is_active;
    });
  }, [currentAiInstructionFilters, aiInstructions]);
};

export const useIsLiveOnlyContentSnippets = () => {
  const contentSnippets = useGetContentSnippets({
    currentContentSnippetId: undefined,
  });
  const currentContentSnippetFilters = useContentSnippetFilters();

  return useMemo(() => {
    return currentContentSnippetFilters.every(filter => {
      const snippet = contentSnippets.find(
        snip => snip.content_snippet_id === filter.contentSnippetId,
      );
      return !filter.isDraft && snippet?.is_active;
    });
  }, [currentContentSnippetFilters, contentSnippets]);
};

export const useAiInstructionPreviewPostMessage = () => {
  const aiInstructionFilters = useAiInstructionFilters();

  const postAiInstructionFiltersToIFrame = useCallback(() => {
    // Add a timeout to wait for the iframe to be fully loaded
    setTimeout(() => {
      const previewIframe = document.getElementById(
        'preview-iframe',
      ) as HTMLIFrameElement;

      const filters = aiInstructionFilters.map(filter => ({
        ai_instruction_id: filter.aiInstructionId,
        is_draft: filter.isDraft,
      }));

      const solveOrigin = new URL(SOLVE_URL).origin;

      previewIframe?.contentWindow?.postMessage(
        {
          aiInstructionFiltersForPreview: filters,
          event: 'updateAiInstructionFiltersForPreview',
        },
        solveOrigin,
      );
    }, 1000); // Wait 1 second before sending the message
  }, [aiInstructionFilters]);

  useEffect(() => {
    postAiInstructionFiltersToIFrame();
  }, [postAiInstructionFiltersToIFrame]);
};

export const useContentSnippetPreviewPostMessage = () => {
  const contentSnippetFilters = useContentSnippetFilters();

  const postContentSnippetFiltersToIFrame = useCallback(() => {
    // Add a timeout to wait for the iframe to be fully loaded
    setTimeout(() => {
      const previewIframe = document.getElementById(
        'preview-iframe',
      ) as HTMLIFrameElement;

      const filters = contentSnippetFilters.map(filter => ({
        content_snippet_id: filter.contentSnippetId,
        is_draft: filter.isDraft,
      }));

      const solveOrigin = new URL(SOLVE_URL).origin;

      previewIframe?.contentWindow?.postMessage(
        {
          contentSnippetFiltersForPreview: filters,
          event: 'updateContentSnippetFiltersForPreview',
        },
        solveOrigin,
      );
    }, 1000); // Wait 1 second before sending the message
  }, [contentSnippetFilters]);

  useEffect(() => {
    postContentSnippetFiltersToIFrame();
  }, [postContentSnippetFiltersToIFrame]);
};
